import { createApiClient } from '../api/client';
import { workspaceKey } from '../lib/workspaceQueryKey';
import type { AdvisorConnection } from '../api/types';

/**
 * The query definition for `GET /advisor/connections` — which firms can read THIS
 * workspace's finances. Extracted so a new observer (today: `useSharedFolders`,
 * `useConnectedFirms`, People → Advisors) reads the exact shape and the exact key rather
 * than risking a second, silently different one — the shape-drift bug class
 * `advisorFirmsQueryOptions` (`useAdvisorFirm.ts`) exists to prevent (steward-app#671:
 * two queryFns on one key, returning different shapes, each stepping on what the other
 * had cached).
 *
 * ⚠️ **Workspace-scoped** (steward-app#1185, closing the last of #845's two exceptions):
 * `workspaceKey('advisor-connections', workspaceId)`, so two workspaces are two cache
 * entries and a switch cannot serve the previous family's connections while the new
 * workspace's entry is cold — see `src/lib/workspaceQueryKey.ts`'s docblock for why the
 * shape matters and `enabled` below for why a null workspace never fetches.
 *
 * Deliberately NOT coerced to `[]` on an unrecognised payload — copied from Advisors.tsx
 * for the same reason stated there: "no advisory firm is connected" is a claim about who
 * can see this family's finances, so an unreadable response must fail loudly (throw, and
 * let the caller decide what "unreadable" should render as) rather than quietly become
 * that claim.
 */
export function advisorConnectionsQueryOptions(token: string | null, workspaceId: string | null) {
  return {
    queryKey: workspaceKey('advisor-connections', workspaceId),
    // ⚠️ `AdvisorConnection`, not `Resource<AdvisorConnectionAttributes>`, since
    // steward-app#1310: the item carries two DERIVED siblings of `attributes` (`advisors`
    // and `engagementStatus`), both optional because both go missing. Widened HERE rather
    // than in one observer's `select`, for this factory's own stated reason — one key, one
    // shape, and a second observer reading a narrower type is how the #671 drift starts.
    queryFn: () => createApiClient(token!).get<{ items: AdvisorConnection[] }>('/advisor/connections')
      .then(r => {
        if (!Array.isArray(r?.items)) throw new Error('unrecognised GET /advisor/connections payload');
        return r.items;
      }),
    // A fetch made before the active workspace resolves would go out under the façade's
    // fallback workspace and be cached under a key that a different family, resolving
    // later in the same tab, would read. See `workspaceQueryKey.ts`'s "`null` is a key,
    // not a hole" section.
    enabled: !!token && !!workspaceId,
  };
}
